import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { PassengerModel } from '@components/forms/select-passenger/select-passenger.component';
import { YYYYMMDD } from '@constants/app.constant';
import { FlightItineraryEnum } from '@constants/flight.constant';
import { FlightInfoFormModel, SearchFormModel } from '@models/flight.model';
import { DropdownListService } from '@services/dropdown-list.service';
import FlightUtils from '@utils/flight.util';
import { convertStringToNumber } from '@utils/number.util';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { map, of } from 'rxjs';

/**
 *  Convert segments
 *
 * @param itinerary
 * @param segments
 */
const convertSegments = (itinerary: number, segments: FlightInfoFormModel[]) => {
    if (!segments.length) {
        return [];
    }

    if (itinerary !== FlightItineraryEnum.MULTI_CITY) {
        const [s1, s2] = segments;
        if (itinerary === FlightItineraryEnum.ONE_WAY) {
            return [s1];
        }
        return [
            {
                ...s1,
                returnDate: s2?.flightDate || null,
            },
        ];
    }
    return segments;
};

const getPassengers = (ps: string) => {
    const [adult, child, infant] = ps.split('.');
    return {
        adult: convertStringToNumber(adult, 0),
        child: convertStringToNumber(child, 0),
        infant: convertStringToNumber(infant, 0),
    };
};

/**
 * Flight resolver
 *
 * @param route
 */
export const flightResolver: ResolveFn<{
    valid: boolean;
    values?: SearchFormModel;
    segments?: FlightInfoFormModel[];
}> = (route) => {
    const dropdownService = inject(DropdownListService);
    const params = route.queryParamMap.keys.reduce(
        (acc, key) => {
            acc[key] = route.queryParamMap.get(key);
            return acc;
        },
        {} as Record<string, string | null>
    );

    if (isEmpty(params)) {
        return of({ valid: false });
    }

    const passenger: PassengerModel = getPassengers(params['ps'] || '');
    if (passenger.adult + passenger.child > 9 || passenger.infant > passenger.adult) {
        return of({ valid: false });
    }
    const itinerary = convertStringToNumber(params['i'], FlightItineraryEnum.ONE_WAY);
    const segments = FlightUtils.getSegmentsFromQueyParam(params['fls'] || '');
    const segmentsForm = convertSegments(itinerary, segments);

    return dropdownService.getAirportMap().pipe(
        map((group) => {
            if (!segmentsForm.length) {
                return false;
            }
            const duplicateDate = FlightUtils.getDuplicateDate(segmentsForm);
            const isRoundTrip = itinerary === FlightItineraryEnum.ROUND_TRIP;

            return segmentsForm.every((s: FlightInfoFormModel) => {
                const { origin, destination, flightDate, returnDate } = s;

                const returnDateEmpty = isRoundTrip && !returnDate;
                if (!origin || !destination || !flightDate || returnDateEmpty) {
                    return false;
                }
                const validOrigin = group[origin];
                const validDestination = group[destination];
                const validFlightDate = moment(flightDate).isValid();
                if (isRoundTrip) {
                    const validReturnDate =
                        returnDate &&
                        moment(returnDate).isValid() &&
                        moment(flightDate).isSameOrBefore(returnDate, 'days');
                    return validReturnDate && validOrigin && validDestination && validFlightDate;
                }

                const key = `${origin}_${destination}_${moment(flightDate).format(YYYYMMDD)}`;
                return validOrigin && validDestination && validFlightDate && key !== duplicateDate;
            });
        }),
        map((valid) => {
            const values = {
                passenger,
                itinerary,
                segments: segmentsForm,
                seat: params['sc'] || '',
            };
            return {
                valid,
                values,
                segments,
            };
        })
    );
};
